import { RiStarFill, RiStarLine } from "@remixicon/react";
import React, {
    useEffect, useMemo,
    useState,
} from "react";
import {
    ArtificialIntelligenceSettingsFeatures,
    ChatStatus, FieldTypedValueBase,
    TicketPriority, useGetAiPromptQuery,
} from "../../../../../__generated__/api";
import useKeyboardShortcuts from "../../../../../contexts/KeyboardShortcutContext";
import usePortal from "../../../../../contexts/PortalContext";
import { TicketItem } from "../../../../../contexts/ticket-state";
import CreateAnswerConfirm from "../../../../answer-creator/CreateAnswer";
import AIQuestion, { AvailableActionResponseAction } from "../../../../artificial-intelligence/AIQuestion";
import Confirm from "../../../../confirm/Confirm";
import { LoadingIcon } from "../../../../loading/Loading";
import Dropdown from "../../../../theme/dropdown/Dropdown";
import Icon from "../../../../theme/icon/Icon";
import Styles from "../TicketDetails.module.scss";
import {
    DetailRow,
    DetailSection,
    TicketProperties, sectionRegistry,
} from "./base";

const MAX_STARS = [1, 2, 3, 4, 5];

function getValue(name: string, value: FieldTypedValueBase) {
    if (name === "Satisfaction" && value.number) {
        const satisfaction = value.number;
        return (
            MAX_STARS.map(index => (
                index <= satisfaction
                    ? <Icon icon={<RiStarFill />} variant="default" size="xsmall" />
                    : <Icon icon={<RiStarLine />} variant="default" size="xsmall" />
            ))
        );
    }
    if (value.displayValue) {
        return value.displayValue;
    }
    if (value.string) {
        return value.string;
    }
    if (value.number != null) {
        return value.number;
    }
    if (value.boolean != null) {
        return value.boolean ? "Yes" : "No";
    }
    if (value.date != null) {
        return value.date.toLocaleString();
    }
    if (value.options != null) {
        return value.options.join(", ");
    }
    return "";
}

interface PromptAction {
    id: string;
    name: string;
    prompt?: string;
    isTicketQuery?: boolean;
    featureName?: keyof ArtificialIntelligenceSettingsFeatures;
    metricPath?: string[];
    handler?: (data: {
        response: string;
        onHide: () => void;
        isShowCreate: boolean;
        confirmMetricPath: string[];
    }) => React.ReactNode;
}

const ASK_AI_PROMPT: PromptAction = {
    id: "ask-ai",
    name: "Ask AI",
    isTicketQuery: true,
    featureName: "isAskAiEnabled",
};
const CREATE_ARTICLE_PROMPT: PromptAction = {
    id: "create-article",
    name: "Create Article from Conversation",
    metricPath: ["create-article"],
    prompt: "Create an article from the user conversation provided. Remove any names, sensitive information, or ticket details. "
        + "The goal of the article is to help others facing a similar issue in the future."
        + "If there is a problem and solution, the article should talk about the problem and solution. "
        + "If the article has instructions on how to do something, the article should be in a step-by-step format. "
        + "The first line should be the title of the article in plain text with no markup. "
        + "The rest should be the HTML article body.",
    isTicketQuery: true,
    featureName: "isCreateArticleEnabled",
    // eslint-disable-next-line react-hooks/rules-of-hooks
    handler: (data: {
        response: string;
        onHide: (() => void);
        isShowCreate: boolean;
        confirmMetricPath: string[];
    }) => {
        const lines = data.response.split("\n");
        return (
            <CreateAnswerConfirm
                title={lines[0]}
                text={lines.skip(1).join("\n")}
                isShowCreate={data.isShowCreate}
                onHide={data.onHide}
                confirmMetricPath={data.confirmMetricPath}
            />
        );
    },
};

const PROMPT_ACTIONS: PromptAction[] = [
    ASK_AI_PROMPT,
    CREATE_ARTICLE_PROMPT,
];

function AICreateArticle({
    ticket, prompt, onHide, confirmMetricPath,
}: {
    ticket: TicketItem | undefined;
    prompt: PromptAction | undefined,
    confirmMetricPath: string[];
    onHide: () => void;
}) {
    const [isShow, setShow] = useState(prompt != null);
    useEffect(() => {
        setShow(prompt != null);
    }, [prompt]);

    const { data, loading: isLoading } = useGetAiPromptQuery({
        skip: !isShow || !prompt,
        fetchPolicy: "network-only",
        variables: prompt ? {
            options: {
                chatId: prompt.isTicketQuery ? ticket?.id : undefined,
                prompt: prompt.prompt as string,
            },
        } : undefined,
    });

    const promptDisplay = useMemo(() => prompt?.handler?.({
        response: data?.artificialIntelligencePrompt?.response ?? "",
        isShowCreate: isShow,
        confirmMetricPath,
        onHide: () => {
            setShow(false);
            onHide();
        },
    }), [data?.artificialIntelligencePrompt?.response, isShow, confirmMetricPath]);

    if (isLoading) {
        return (
            <Confirm
                title="Generating..."
                shouldShow
                size="sm"
                onHide={() => setShow(false)}
                bodyClassName="text-center overflow-hidden"
            >
                <LoadingIcon />
            </Confirm>
        );
    }
    return promptDisplay;
}

const NEW_STATUSES: Record<string, ChatStatus> = {
    unassigned: ChatStatus.ChatRequested,
    started: ChatStatus.Assigned,
    closed: ChatStatus.Finished,
};

const NEW_PRIORITY: Record<string, TicketPriority> = {
    CRITICAL: TicketPriority.Critical,
    HIGH: TicketPriority.High,
    MEDIUM: TicketPriority.Medium,
    LOW: TicketPriority.Low,
};

const AVAILABLE_ACTIONS = [
    {
        id: "append-response",
        name: "Append to Response Draft",
        prompt: "Appends the provided responseMessage to the response draft that the agent will respond to the customer. This should be used when a user is asking anything about a response to the customer. The message could be in HTML format.",
        parameters: [
            {
                name: "responseMessage",
                description: "The response to append",
            },
        ],
        invoke(ticket: TicketItem, response: Record<string, string>) {
            type DraftProperties = Parameters<(typeof ticket)["saveDraft"]>[0]
            ticket.saveDraftWithUpdate({
                ...ticket.latestDraft as DraftProperties,
                body: `${ticket.latestDraft?.body ?? ""}\n\n${response.responseMessage ?? ""}`.trim(),
            });
        },
    },
    {
        id: "replace-response",
        name: "Replace Response Draft",
        prompt: "Replaces the current response draft with the provided responseMessage. This should be used when a user is asking anything about a response to the customer. This is helpful if the user response wants a whole new response. The message could be in HTML format.",
        parameters: [
            {
                name: "responseMessage",
                description: "The response to append",
            },
        ],
        invoke(ticket: TicketItem, response: Record<string, string>) {
            type DraftProperties = Parameters<(typeof ticket)["saveDraft"]>[0]
            ticket.saveDraftWithUpdate({
                ...ticket.latestDraft as DraftProperties,
                body: response.responseMessage?.trim(),
            });
        },
    },
    {
        id: "change-status",
        name: "Change Status",
        prompt: "Change the ticket status to one of the following (No other statuses exist other "
                    + "than the following): unassigned, started, or closed.",
        parameters: [
            {
                name: "newStatus",
                description: "One of the available statuses",
            },
        ],
        invoke(ticket: TicketItem, response: Record<string, string>) {
            const newStatus = NEW_STATUSES[response.newStatus];
            ticket.setStatus(newStatus);
            ticket.focus();
        },
    },
    {
        id: "update-ticket",
        name: "Update Ticket",
        prompt: "Allows the assistant to update ticket properties based on available parameters. "
        + "Multiple parameters can be updated together or one at a time",
        parameters: [
            {
                name: "subject",
                description: "The subject of the ticket",
            },
            {
                name: "description",
                description: "The description of the ticket",
            },
            {
                name: "priority",
                description: "The priority of the ticket. It can only be one of the following: low, medium, high, critical",
            },
        ],
        invoke(ticket: TicketItem, response: Record<string, string>) {
            ticket.saveTicket({
                subject: response.subject,
                description: response.description,
                priority: NEW_PRIORITY[response.priority?.toUpperCase()],
            });
        },
    },
];

const KEYBOARD_CONTEXT = "chat";
export default function ArtificialIntelligenceSection({
    ticket, isMinimized, onToggleExpand, isReadOnly, metricPathPrefix,
}: TicketProperties) {
    const { isFirstLoading } = ticket.useChatDetails({ isTicketOnly: isReadOnly });

    const { entitlementIds, aiFeatures } = usePortal();

    const [isShowMenu, setShowMenu] = useState(false);
    const [prompt, setPrompt] = useState<PromptAction>();

    const promptActions = useMemo(
        () => PROMPT_ACTIONS.filter(action => !action.featureName || aiFeatures[action.featureName] !== false),
        [aiFeatures],
    );

    const metricPath = useMemo(() => [...metricPathPrefix, "ai"], metricPathPrefix);

    useKeyboardShortcuts(api => {
        const askAi = promptActions.find(action => action.id === "ask-ai");
        if (askAi) {
            api.on({
                context: KEYBOARD_CONTEXT,
                shortcut: "alt+space",
                group: "AI Assistant",
                handler: () => {
                    setPrompt(ASK_AI_PROMPT);
                },
            });
        }
    });

    const values = ticket.ticket.artificialIntelligence?.values?.filter(row => row.isEndUserVisible !== false);
    if (!entitlementIds.has(entitlementIds.keys.ARTIFICIAL_INTELLIGENCE)
        || !values?.isNotEmpty) {
        return null;
    }

    const invokeAction = (action: AvailableActionResponseAction) => {
        AVAILABLE_ACTIONS.find(current => current.id === action.id)?.invoke(ticket, action.parameters as Record<string, string>);
    };

    return (
        <>
            <AIQuestion
                onHide={() => setPrompt(undefined)}
                shouldShow={prompt?.id === "ask-ai"}
                chatId={ticket.id}
                availableActions={AVAILABLE_ACTIONS}
                metricPathPrefix={metricPath}
                invokeAction={invokeAction}
                additionalContext="You are a AI bot helping the agent with their support tickets.
                        Your goal is to help the agent resolve this ticket as effiently as possible. You can use the
                        information provided and any outside information to help the user resolve the issue.
                        Your window is overlaying the ticket so you only want to be visible when helping the agent. If the
                        agent seems to be don e, give them the option to close the AI window so they can handle the ticket
                        more manually"
            />
            {prompt?.id !== "ask-ai" && (
                <AICreateArticle
                    confirmMetricPath={prompt ? [...metricPath, "action", prompt?.id, "confirm"] : []}
                    prompt={prompt}
                    ticket={ticket}
                    onHide={() => setPrompt(undefined)}
                />
            )}

            <DetailSection
                title={(
                    <div className={Styles["details-header"]}>
                        <div>
                            AI Assist
                        </div>
                        {promptActions.isNotEmpty && (

                            <Dropdown onToggle={() => setShowMenu(!isShowMenu)} show={isShowMenu}>
                                <Dropdown.Toggle
                                    prefixIcon={<Icon.ArtificialIntelligence />}
                                    size="xsmall"
                                    variant="ghost-outline-blue"
                                    onClick={evt => {
                                        evt.stopPropagation();
                                        setShowMenu(!isShowMenu);
                                    }}
                                >
                                    Actions
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    {promptActions.map(action => (
                                        <Dropdown.Item
                                            key={action.id}
                                            metricPath={action.metricPath
                                                ? metricPath.concat(["action"], action.metricPath)
                                                : undefined}
                                            onClick={evt => {
                                                evt.stopPropagation();
                                                setShowMenu(false);
                                                setPrompt(action);
                                            }}
                                        >
                                            {action.name}
                                        </Dropdown.Item>
                                    ))}

                                </Dropdown.Menu>
                            </Dropdown>
                        )}
                    </div>
                )}
                isMinimized={isMinimized}
                onToggleExpand={onToggleExpand}
                className={isFirstLoading ? "loading" : Styles["section-full"]}
            >
                {isFirstLoading ? (
                    <DetailRow title="Summary">
                        Loading...
                    </DetailRow>
                ) : (ticket.ticket.artificialIntelligence?.values?.filterMap(aiValue => (
                    <DetailRow
                        title={aiValue.name}
                        description={aiValue.description as string}
                        className="multiline"
                        key={aiValue.name}
                    >
                        {getValue(aiValue.name as string, aiValue.value)}
                    </DetailRow>
                ), aiValue => aiValue.isEndUserVisible !== false))}

            </DetailSection>
        </>

    );
}

sectionRegistry.register({
    sectionId: "urn:fq:portal-chat-section:artificial-intelligence",
    label: "AI Assist",
    component: ArtificialIntelligenceSection,
});
